// src/components/ReportProcessing.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertTriangle, CheckCircle, Lightbulb } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrochip } from '@fortawesome/free-solid-svg-icons';
import './ReportProcessing.css';

const REACT_APP_API_BASE = process.env.REACT_APP_API_BASE;
const REACT_APP_COUNSELLING_API_BASE = process.env.REACT_APP_COUNSELLING_API_BASE;

const ReportProcessing = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const reportId = searchParams.get('id');

    const [status, setStatus] = useState('pending');
    const [errorMessage, setErrorMessage] = useState('');
    
    // Timer state
    const TOTAL_SECONDS = 300; // 5 minutes
    const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
    const [isTimeout, setIsTimeout] = useState(false);

    // State for loading facts
    const [facts, setFacts] = useState([]);
    const [currentFactIndex, setCurrentFactIndex] = useState(0);
    const [factIsVisible, setFactIsVisible] = useState(true);

    // Effect for the countdown timer
    useEffect(() => {
        if (status === 'pending' || status === 'processing') {
            if (secondsLeft <= 0) {
                setIsTimeout(true);
                return;
            }
            const timerId = setTimeout(() => setSecondsLeft(secondsLeft - 1), 1000);
            return () => clearTimeout(timerId);
        }
    }, [secondsLeft, status]);

    // Effect to fetch loading facts
    useEffect(() => {
        const fetchFacts = async () => {
            try {
                const { data } = await axios.get(`${REACT_APP_COUNSELLING_API_BASE}/api/loading-facts`);
                if (data && data.length > 0) {
                    setFacts(data);
                }
            } catch (error) {
                console.error("Failed to fetch loading facts:", error);
            }
        };
        fetchFacts();
    }, []);

    // Effect to cycle through facts with a fade animation
    useEffect(() => {
        if (facts.length > 0 && (status === 'pending' || status === 'processing')) {
            const interval = setInterval(() => {
                setFactIsVisible(false); // Start fade-out

                setTimeout(() => {
                    setCurrentFactIndex(prevIndex => (prevIndex + 1) % facts.length);
                    setFactIsVisible(true); // Start fade-in with new content
                }, 500); // Wait for fade-out to complete
            }, 7000); // Change fact every 7 seconds

            return () => clearInterval(interval);
        }
    }, [facts, status]);

    // Effect for polling report status
    useEffect(() => {
        if (!reportId) {
            setStatus('failed');
            setErrorMessage('No report ID specified. You will be redirected.');
            setTimeout(() => navigate('/dashboard?view=get-report'), 4000);
            return;
        }

        const pollStatus = async () => {
            try {
                const { data } = await axios.get(`${REACT_APP_API_BASE}/api/reports/status/${reportId}`);
                
                if (data.status === 'completed' || data.status === 'failed') {
                    setStatus(data.status);
                    if (data.status === 'completed') {
                        setTimeout(() => navigate(`/dashboard?view=report-result&id=${reportId}`), 2000);
                    } else {
                        setErrorMessage(data.errorMessage || 'An unknown error occurred.');
                    }
                    return null; // Stop polling
                }

                setStatus(data.status);
                return pollStatus;
            } catch (err) {
                setStatus('failed');
                setErrorMessage(err.response?.data?.message || 'Could not check status.');
                return null;
            }
        };

        const startPolling = async () => {
          let nextPoll = await pollStatus();
          const intervalId = setInterval(async () => {
            if (nextPoll) {
              nextPoll = await nextPoll();
            } else {
              clearInterval(intervalId);
            }
          }, 5000);
          
          return () => clearInterval(intervalId);
        };
        
        startPolling();
    }, [reportId, navigate]);

    const formatTime = (totalSeconds) => {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    const renderContent = () => {
        switch (status) {
            case 'completed':
                return (
                    <>
                        <CheckCircle className="status-icon success" size={80} />
                        <h2 className="processing-title">Report Generated!</h2>
                        <p className="processing-subtitle">Your AI-powered report is ready. Redirecting you now...</p>
                    </>
                );
            case 'failed':
                return (
                    <>
                        <AlertTriangle className="status-icon error" size={80} />
                        <h2 className="processing-title">Generation Failed</h2>
                        <p className="processing-subtitle">We're sorry, but an error occurred.</p>
                        <div className="error-message">Details: {errorMessage}</div>
                        <Link to="/dashboard?view=get-report" className="nav-button nav-button-primary">Try Again</Link>
                    </>
                );
            case 'processing':
            case 'pending':
            default:
                if (isTimeout) {
                    return (
                        <>
                            <div className="processing-animation-container">
                                <Loader2 className="status-icon loading" size={96} />
                                <FontAwesomeIcon icon={faMicrochip} className="processing-main-icon" />
                            </div>
                            <h2 className="processing-title">Still working on it...</h2>
                            <p className="processing-subtitle">Processing is taking longer than expected. Thanks for your patience.</p>
                            <p className="processing-footer">The page will redirect automatically upon completion.</p>
                        </>
                    );
                }
                return (
                    <>
                        <div className="processing-animation-container">
                            <Loader2 className="status-icon loading" size={96} />
                            <FontAwesomeIcon icon={faMicrochip} className="processing-main-icon" />
                        </div>
                        
                        <h2 className="processing-title">Your Report is Generating...</h2>
                        <p className="processing-subtitle">GNVISA is analyzing your profile. This may take a few minutes.</p>
                        
                        {facts.length > 0 && (
                            <div className={`loading-fact ${factIsVisible ? 'visible' : ''}`}>
                                <Lightbulb className="fact-icon" size={20} />
                                <p>{facts[currentFactIndex]?.text}</p>
                            </div>
                        )}
                        
                        <div className="timer-display">
                            Estimated time remaining: {formatTime(secondsLeft)}
                        </div>
                        <p className="processing-footer">Please keep this page open. You will be redirected automatically.</p>
                    </>
                );
        }
    };

    return (
        <div className="processing-container">
            <div className="processing-card">
                {/* Content is rendered first */}
                {renderContent()}
                
                {/* Report ID is now at the bottom */}
                {reportId && <div className="report-id-display-bottom">Report ID: {reportId}</div>}
            </div>
        </div>
    );
};

export default ReportProcessing;